import React from 'react';
import { 
  Mail, 
  Server, 
  User, 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Loader,
  AlertCircle,
  Save,
  TestTube,
  Shield,
  Key,
  Settings as SettingsIcon,
  Eye,
  EyeOff,
  PenTool,
  Star,
  Upload
} from 'lucide-react';
import { useAuth } from '../Auth/AuthContext';
import { useTranslation } from 'react-i18next';
import EmailSignatureManager from './EmailSignatureManager';
import EmailImporter from './EmailImporter';
import EmailTemplatePreview from './EmailTemplatePreview';
import MailAccountModal from './MailAccountModal';
import logger from '../../utils/logger';

import { useMailSettings } from './useMailSettings';

const MailSettings = ({ backendUrl, authToken }) => {
  const { t } = useTranslation('admin');
  const {
    user,
    activeSection,
    setActiveSection,
    loading,
    setLoading,
    saving,
    setSaving,
    showImapPassword,
    setShowImapPassword,
    showSmtpPassword,
    setShowSmtpPassword,
    testing,
    setTesting,
    testResult,
    setTestResult,
    emailAccounts,
    setEmailAccounts,
    showAccountModal,
    setShowAccountModal,
    editingAccount,
    setEditingAccount,
    accountForm,
    setAccountForm,
    admins,
    setAdmins,
    successMessage,
    setSuccessMessage,
    errorMessage,
    setErrorMessage,
    loadInitialData,
    loadEmailAccounts,
    loadAdmins,
    testConnection,
    saveEmailAccount,
    deleteEmailAccount,
    setDefaultEmailAccount,
    openAccountModal,
    closeAccountModal,
    showSuccess,
    showError
  } = useMailSettings(backendUrl, authToken);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{t('mail.ms.title')}</h2>
          <p className="text-slate-600 mt-1">{t('mail.ms.subtitle')}</p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center">
          <CheckCircle className="text-emerald-600 mr-3" size={20} />
          <span className="text-emerald-700">{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
          <XCircle className="text-red-600 mr-3" size={20} />
          <span className="text-red-700">{errorMessage}</span>
        </div>
      )}

      {/* Section Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-1">
        <div className="flex space-x-1">
          <button
            onClick={() => setActiveSection('individual')}
            className={`flex-1 flex items-center justify-center px-6 py-3 rounded-xl transition-all ${
              activeSection === 'individual'
                ? 'bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 shadow-sm border border-indigo-100'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <User size={18} className="mr-2" />
            <span className="font-medium">{t('mail.ms.tabIndividual')}</span>
          </button>

          <button
            onClick={() => setActiveSection('shared')}
            className={`flex-1 flex items-center justify-center px-6 py-3 rounded-xl transition-all ${
              activeSection === 'shared'
                ? 'bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 shadow-sm border border-indigo-100'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Users size={18} className="mr-2" />
            <span className="font-medium">{t('mail.ms.tabShared')}</span>
          </button>

          <button
            onClick={() => setActiveSection('signatures')}
            className={`flex-1 flex items-center justify-center px-6 py-3 rounded-xl transition-all ${
              activeSection === 'signatures'
                ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 shadow-sm border border-purple-100'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <PenTool size={18} className="mr-2" />
            <span className="font-medium">{t('mail.ms.tabSignatures')}</span>
          </button>

          <button
            onClick={() => setActiveSection('import')}
            className={`flex-1 flex items-center justify-center px-6 py-3 rounded-xl transition-all ${
              activeSection === 'import'
                ? 'bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 shadow-sm border border-emerald-100'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Upload size={18} className="mr-2" />
            <span className="font-medium">{t('mail.ms.tabImport')}</span>
          </button>

          <button
            onClick={() => setActiveSection('templates')}
            data-testid="mail-tab-templates"
            className={`flex-1 flex items-center justify-center px-6 py-3 rounded-xl transition-all ${
              activeSection === 'templates'
                ? 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 shadow-sm border border-amber-100'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Mail size={18} className="mr-2" />
            <span className="font-medium">{t('mail.ms.tabTemplates')}</span>
          </button>
        </div>
      </div>

      {/* Individual Emails Section */}
      {activeSection === 'individual' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-900 flex items-center">
              <User className="mr-2 text-indigo-600" size={24} />
              {t('mail.ms.individualTitle')}
            </h3>
            <button
              onClick={() => openAccountModal()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>{t('mail.ms.addEmail')}</span>
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">{t('mail.ms.colEmail')}</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">{t('mail.ms.colDisplayName')}</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">{t('mail.ms.colAdmin')}</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">{t('mail.ms.colStatus')}</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-slate-500 uppercase">{t('mail.ms.colActions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {emailAccounts.filter(acc => acc.account_type === 'individual').map((account) => (
                  <tr key={account.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{account.email_address}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{account.display_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                      {admins.find(a => a.id === account.assigned_admin_id)?.first_name || t('mail.ms.notAssigned')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        account.is_active 
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                          : 'bg-slate-50 text-slate-700 border border-slate-200'
                      }`}>
                        {account.is_active ? t('mail.ms.active') : t('mail.ms.inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openAccountModal(account)}
                        className="text-indigo-600 hover:text-indigo-800 mr-3"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => deleteEmailAccount(account.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Shared Emails Section */}
      {activeSection === 'shared' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-900 flex items-center">
              <Users className="mr-2 text-indigo-600" size={24} />
              {t('mail.ms.sharedTitle')}
            </h3>
            <button
              onClick={() => {
                setAccountForm({...accountForm, account_type: 'shared'});
                openAccountModal();
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>{t('mail.ms.addSharedEmail')}</span>
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">{t('mail.ms.colEmail')}</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">{t('mail.ms.colDisplayName')}</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">{t('mail.ms.colAuthorizedAdmins')}</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">{t('mail.ms.colStatus')}</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase">{t('mail.ms.colDefault')}</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-slate-500 uppercase">{t('mail.ms.colActions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {emailAccounts.filter(acc => acc.account_type === 'shared').map((account) => (
                  <tr key={account.id} className={`hover:bg-slate-50 transition-colors ${account.is_default ? 'bg-amber-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div className="flex items-center gap-2">
                        {account.is_default && <Star size={14} className="text-amber-500 fill-amber-500" />}
                        {account.email_address}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{account.display_name}</td>
                    <td className="px-6 py-4 text-sm text-slate-700">
                      {t('mail.ms.adminsCount', { count: account.authorized_admin_ids?.length || 0 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        account.is_active 
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                          : 'bg-slate-50 text-slate-700 border border-slate-200'
                      }`}>
                        {account.is_active ? t('mail.ms.active') : t('mail.ms.inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {account.is_default ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                          <Star size={12} className="fill-amber-500" />
                          {t('mail.ms.defaultBadge')}
                        </span>
                      ) : (
                        <button
                          onClick={() => setDefaultEmailAccount(account.id)}
                          className="text-xs text-slate-500 hover:text-amber-600 hover:bg-amber-50 px-3 py-1 rounded-full border border-slate-200 hover:border-amber-300 transition-colors"
                        >
                          {t('mail.ms.setDefault')}
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openAccountModal(account)}
                        className="text-indigo-600 hover:text-indigo-800 mr-3"
                        title={t('mail.ms.editTitle')}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => deleteEmailAccount(account.id)}
                        className="text-red-600 hover:text-red-800"
                        title={t('mail.ms.deleteTitle')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Signatures Section */}
      {activeSection === 'signatures' && (
        <EmailSignatureManager 
          backendUrl={backendUrl}
          authToken={authToken}
          emailAccounts={emailAccounts}
        />
      )}

      {/* Import Section */}
      {activeSection === 'import' && (
        <EmailImporter 
          backendUrl={backendUrl}
          authToken={authToken}
          emailAccounts={emailAccounts}
        />
      )}

      {/* Email Templates Section */}
      {activeSection === 'templates' && (
        <EmailTemplatePreview
          backendUrl={backendUrl}
          authToken={authToken}
        />
      )}

      {/* Account Modal */}
      {showAccountModal && (
        <MailAccountModal
          saving={saving}
          showImapPassword={showImapPassword}
          setShowImapPassword={setShowImapPassword}
          showSmtpPassword={showSmtpPassword}
          setShowSmtpPassword={setShowSmtpPassword}
          testing={testing}
          testResult={testResult}
          editingAccount={editingAccount}
          accountForm={accountForm}
          setAccountForm={setAccountForm}
          admins={admins}
          successMessage={successMessage}
          errorMessage={errorMessage}
          testConnection={testConnection}
          saveEmailAccount={saveEmailAccount}
          closeAccountModal={closeAccountModal}
        />
      )}
    </div>
  );
};

export default MailSettings;
